import Icon from '@/components/common/icons/Icon';
import { Center, CenterProps } from '@chakra-ui/react';
import {
    AnyFinancialEntity,
    PlentyGoalAccountFinancialEntity,
} from '@common/financialEntities';
import { getColor, getIconName } from '@common/react/financialEntities';
import { iconNameByPlentyGoalAccountType } from '@common/react/iconNameByPlentyGoalAccountType';
import { IconSVGComponentProps } from '../common/icons/IconComponent';
import { GoalIconBase } from '../goals/GoalIcon';
import { getLogoImage } from './helpers';

export interface FinancialEntityIconProps<FE extends AnyFinancialEntity>
    extends CenterProps {
    fe: FE;
    size?: CenterProps['w'];
}

export function FinancialEntityIcon<FE extends AnyFinancialEntity>({
    fe,
    rounded = 'full',
    size = 5,
    ...otherProps
}: FinancialEntityIconProps<FE>) {
    // ...otherProps => props stutter to set default props for both components
    const props = {
        rounded,
        w: size,
        h: size,
        minW: size,
        maxW: size,
        minH: size,
        maxH: size,
        ...otherProps,
    };

    // Return fixed icon for PlentyGoalAccount
    if (fe.type === 'PlentyGoalAccount') {
        return (
            <Center pos="relative" {...props}>
                <GoalAccountIcon size="md" fe={fe} />
            </Center>
        );
    }

    // Return logo image for linked institution if we have it
    const LogoImage = getLogoImage(fe);
    if (LogoImage) {
        return (
            <Center {...props} pos="relative">
                <LogoImage />
            </Center>
        );
    }

    // Otherwise, just return the default icon for this type
    const iconName = getIconName(fe.type);
    const bgColor = getColor(fe.type);
    return (
        <Center bg={bgColor} color="white" pos="relative" {...props}>
            <Icon name={iconName} size="sm" />
        </Center>
    );
}

interface GoalAccountIconProps extends Omit<IconSVGComponentProps, 'style'> {
    fe: PlentyGoalAccountFinancialEntity;
}

const GoalAccountIcon = ({ fe, ...props }: GoalAccountIconProps) => {
    // Always show appropriate IRA icon for IRA goal accounts.
    const iconName = iconNameByPlentyGoalAccountType[fe.info.accountType];
    if (['ROTH_IRA', 'TRADITIONAL_IRA'].includes(fe.info.accountType)) {
        return <Icon name={iconName} {...props} />;
    }

    // Otherwise, show goal icon if goal data is available.
    if (fe.goal?.type) {
        return <GoalIconBase {...props} type={fe.goal.type} progress={null} />;
    }

    // As a fallback, show the generic icon for this goal account type.
    return <Icon name={iconName} {...props} />;
};
